import type { ComponentProps } from 'react';
import React from 'react';
import { gql } from '@soundxyz/gql-string';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { DescriptionSelector } from '@components/main-components/selectors/description-selector';
import { List, Tabs, Trigger } from '@components/main-components/tabs/Tab';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { BondingCurveMintComponentFragmentDoc } from '@graphql/reactQuery';
import { DataTestId } from '@utils/test';
import { CollectInfo } from './CollectInfo';
import { SellInfo } from './SellInfo';

export const TAB_CHOICES = {
  COLLECT: 'collect',
  SELL: 'sell',
} as const;

export type TAB_CHOICES = (typeof TAB_CHOICES)[keyof typeof TAB_CHOICES];

export const BondingTabValues = Object.values(TAB_CHOICES);

export function getBondingTab(value: unknown) {
  return BondingTabValues.find(v => v === value) || null;
}

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment BondingCurveMintComponent on Release {
    id
    ...SamCollectInfo
    ...SamSellInfo
  }
`);

export const BondingCurveMintComponent = ({
  data,
  editionsOwned = 0,
  maxLiquidationThreshold = 0,
  hasGoldenEgg = false,
  collectButtonSource,
  DO_NOT_USE_IN_PROD_defaultTab: testDefaultTab,
}: {
  data: FragmentType<BondingCurveMintComponentFragmentDoc>;
  collectButtonSource: CollectButtonSource;
  /** This is only used for testing purposes */
  DO_NOT_USE_IN_PROD_defaultTab?: TAB_CHOICES;
} & Pick<
  ComponentProps<typeof SellInfo>,
  'hasGoldenEgg' | 'maxLiquidationThreshold' | 'editionsOwned'
>) => {
  const release = getFragment(BondingCurveMintComponentFragmentDoc, data);
  const [selectedTab, setSelectedTab] = React.useState<TAB_CHOICES>(
    testDefaultTab || TAB_CHOICES.COLLECT,
  );

  return (
    <Tabs
      className="z-above1 flex w-full flex-col  rounded-b-md bg-white p-4"
      value={getBondingTab(selectedTab) || TAB_CHOICES.COLLECT}
      defaultValue={testDefaultTab || TAB_CHOICES.COLLECT}
      onValueChange={value => setSelectedTab(getBondingTab(value) || TAB_CHOICES.COLLECT)}
      data-state={selectedTab}
      data-testid={DataTestId.BONDING_CURVE_MINT_COMPONENT}
    >
      <List className="flex w-full justify-between gap-2.5 ">
        <Trigger className="flex w-full" asChild value={TAB_CHOICES.COLLECT}>
          <DescriptionSelector
            className="flex h-[54px] w-full items-center text-center "
            selected={selectedTab === TAB_CHOICES.COLLECT}
            data-testid={DataTestId.BONDING_CURVE_MINT_COMPONENT_COLLECT}
          >
            {TAB_CHOICES.COLLECT}
          </DescriptionSelector>
        </Trigger>
        <Trigger className="flex w-full" asChild value={TAB_CHOICES.SELL}>
          <DescriptionSelector
            className="flex h-[54px] w-full items-center text-center"
            selected={selectedTab === TAB_CHOICES.SELL}
            data-testid={DataTestId.BONDING_CURVE_MINT_COMPONENT_SELL}
          >
            {TAB_CHOICES.SELL}
          </DescriptionSelector>
        </Trigger>
      </List>
      <CollectInfo data={release} collectButtonSource={collectButtonSource} />
      <SellInfo
        data={release}
        maxLiquidationThreshold={maxLiquidationThreshold}
        editionsOwned={editionsOwned}
        hasGoldenEgg={hasGoldenEgg}
      />
    </Tabs>
  );
};
